import React, { useState, useCallback, useEffect, useRef } from "react";
import { TextInput, Select, Group, Stack, Text, Tooltip } from "@mantine/core";
import { US_STATES, US_TIMEZONES } from "../../../utils/formConstants";
import {
  AboutYourBusinessData,
  BusinessBasicsData,
  ABOUT_YOUR_BUSINESS_QUESTIONS,
  BUSINESS_BASICS_QUESTIONS,
  BUSINESS_VERTICAL_OPTIONS,
  STAFFING_MODEL_OPTIONS,
  normalizeBusinessVerticalValue,
  QUESTION_KEYS,
} from "../types/onboarding";
import ChipGroupSingle from "../components/ChipGroupSingle";
import StandardizedPhoneInput, {
  ParsedPhoneNumber,
} from "../../common/StandardizedPhoneInput";
import styles from "./AboutYourBusinessStepContent.module.css";

interface AboutYourBusinessStepContentProps {
  data: AboutYourBusinessData;
  onChange: (
    data: Partial<AboutYourBusinessData>,
    questionKey?: string,
    questionValue?: string,
    answerValue?: string,
  ) => void;
  businessBasicsData: BusinessBasicsData;
  onBusinessBasicsChange: (
    data: Partial<BusinessBasicsData>,
    questionKey?: string,
    questionValue?: string,
    answerValue?: string,
  ) => void;
}

export default function AboutYourBusinessStepContent({
  data,
  onChange,
  businessBasicsData,
  onBusinessBasicsChange,
}: AboutYourBusinessStepContentProps) {
  // Track if we've already attempted to set the default timezone
  const hasSetDefaultTimezone = useRef(false);

  // Set default timezone on mount if browser timezone exactly matches a US timezone
  // Only set if no timezone is currently set
  useEffect(() => {
    // Only run once on mount, and only if timezone is not already set
    if (!hasSetDefaultTimezone.current) {
      hasSetDefaultTimezone.current = true;

      // If timezone is already set (e.g., from saved answers), don't override it
      if (data.timezone && data.timezone.trim()) {
        return;
      }

      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Check if browser timezone exactly matches one of our US timezones
      const matchingTimezone = US_TIMEZONES.find(
        (tz) => tz.value === browserTimezone,
      );

      if (matchingTimezone) {
        // Browser timezone matches a US timezone - set it as default
        // This will update the state and allow validation to pass
        onChange({ timezone: matchingTimezone.value });
        // Save the default timezone as an answer
        onChange(
          {},
          QUESTION_KEYS.WHAT_IS_YOUR_TIMEZONE,
          "What is your Timezone?",
          matchingTimezone.value,
        );
      }
      // If browser timezone doesn't match, don't set default - user must select
    }
  }, [data.timezone, onChange]);

  // Get question configs
  const businessNameQuestion = ABOUT_YOUR_BUSINESS_QUESTIONS.find(
    (q) => q.questionKey === QUESTION_KEYS.WHATS_YOUR_BUSINESS_NAME,
  );
  const businessVerticalQuestion = BUSINESS_BASICS_QUESTIONS.find(
    (q) => q.questionKey === QUESTION_KEYS.WHAT_IS_YOUR_BUSINESS_VERTICAL,
  );
  const staffingModelQuestion = BUSINESS_BASICS_QUESTIONS.find(
    (q) => q.questionKey === QUESTION_KEYS.WHAT_IS_YOUR_STAFFING_MODEL,
  );
  const businessAddressQuestion = ABOUT_YOUR_BUSINESS_QUESTIONS.find(
    (q) => q.questionKey === QUESTION_KEYS.WHATS_YOUR_BUSINESS_ADDRESS,
  );
  const businessPhoneQuestion = ABOUT_YOUR_BUSINESS_QUESTIONS.find(
    (q) => q.questionKey === QUESTION_KEYS.WHATS_YOUR_BUSINESS_PHONE_NUMBER,
  );
  const employeeCountQuestion = ABOUT_YOUR_BUSINESS_QUESTIONS.find(
    (q) => q.questionKey === QUESTION_KEYS.HOW_MANY_EMPLOYEES_DO_YOU_HAVE,
  );

  // Build formatted phone value from areaCode and phoneNumber
  const formattedPhoneValue =
    data.areaCode && data.phoneNumber
      ? `(${data.areaCode}) ${data.phoneNumber.slice(0, 3)}-${data.phoneNumber.slice(3)}`
      : "";

  // Handle phone number formatted value change
  const handlePhoneChange = useCallback((formattedValue: string) => {
    // The formatted value is handled by onParsedChange
    // This is just to keep the component in sync
  }, []);

  // Handle parsed phone number change (for salon/business format)
  // Always set country code to "1" for business info
  const handlePhoneParsedChange = useCallback(
    (parsed: ParsedPhoneNumber | null) => {
      // Only process if parsed is valid and has both areaCode and phoneNumber
      if (parsed && parsed.areaCode && parsed.phoneNumber) {
        onChange({
          countryCode: "1", // Always assume country code is 1 for business
          areaCode: parsed.areaCode,
          phoneNumber: parsed.phoneNumber,
        });
        // Save individual fields - only save if values are valid
        onChange(
          {},
          QUESTION_KEYS.BUSINESS_PHONE_COUNTRY_CODE,
          "Country Code",
          "1",
        );
        if (parsed.areaCode) {
          onChange(
            {},
            QUESTION_KEYS.BUSINESS_PHONE_AREA_CODE,
            "Area Code",
            parsed.areaCode,
          );
        }
        if (parsed.phoneNumber) {
          onChange(
            {},
            QUESTION_KEYS.BUSINESS_PHONE_NUMBER,
            "Phone Number",
            parsed.phoneNumber,
          );
        }
      }
    },
    [onChange],
  );

  // Handle text input blur (save on blur for text fields)
  const handleTextBlur = (
    questionKey: string,
    questionText: string,
    value: string,
  ) => {
    if (value) {
      onChange({}, questionKey, questionText, value);
    }
  };

  // Label styles with font-weight 400
  const labelStyles = {
    label: {
      fontWeight: 400,
    },
  };

  const normalizedBusinessVertical = normalizeBusinessVerticalValue(
    businessBasicsData.businessVertical,
  );
  const showStaffingModel = !!staffingModelQuestion;

  return (
    <Stack className={styles.container}>
      <Stack gap="lg" className={styles.scrollableContent}>
        {/* What's your business name? */}
        <TextInput
          label={
            businessNameQuestion?.questionText || "What's your business name?"
          }
          placeholder="Business Name"
          value={data.businessName}
          onChange={(e) => onChange({ businessName: e.currentTarget.value })}
          onBlur={(e) =>
            handleTextBlur(
              QUESTION_KEYS.WHATS_YOUR_BUSINESS_NAME,
              businessNameQuestion?.questionText ||
                "What's your business name?",
              e.currentTarget.value,
            )
          }
          required
          size="md"
          // Without an explicit token, browsers offered the user's EMAIL as
          // autofill here — one of the audited name-was-an-email channels.
          autoComplete="organization"
          classNames={{ input: styles.input }}
          styles={labelStyles}
        />

        {/* What type of business? (business vertical) */}
        {businessVerticalQuestion && (
          <ChipGroupSingle
            label={businessVerticalQuestion.questionText}
            options={BUSINESS_VERTICAL_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
            value={normalizedBusinessVertical}
            onChange={(value) => {
              // Clear staffing model when vertical changes
              const slug = normalizeBusinessVerticalValue(value ?? undefined);
              onBusinessBasicsChange(
                {
                  businessVertical: slug,
                  staffingModel: null,
                },
                QUESTION_KEYS.WHAT_IS_YOUR_BUSINESS_VERTICAL,
                businessVerticalQuestion.questionText,
                slug ?? "",
              );
            }}
            required
          />
        )}

        {/* What is your staffing model? */}
        {showStaffingModel && (
          <ChipGroupSingle
            label={staffingModelQuestion.questionText}
            options={STAFFING_MODEL_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
            value={businessBasicsData.staffingModel}
            onChange={(value) =>
              onBusinessBasicsChange(
                { staffingModel: value },
                QUESTION_KEYS.WHAT_IS_YOUR_STAFFING_MODEL,
                staffingModelQuestion.questionText,
                value || "",
              )
            }
            required
          />
        )}

        {/* What's your business address? */}
        <TextInput
          label={
            businessAddressQuestion?.questionText ||
            "What's your business address?"
          }
          placeholder="Business Address"
          value={data.addressLine1}
          onChange={(e) => onChange({ addressLine1: e.currentTarget.value })}
          onBlur={(e) =>
            handleTextBlur(
              QUESTION_KEYS.WHATS_YOUR_BUSINESS_ADDRESS,
              businessAddressQuestion?.questionText ||
                "What's your business address?",
              e.currentTarget.value,
            )
          }
          required
          size="md"
          classNames={{ input: styles.input }}
          styles={labelStyles}
        />

        {/* Address Line 2 (Optional) */}
        <TextInput
          placeholder="Apt., suite, etc (Optional)"
          value={data.addressLine2}
          onChange={(e) => onChange({ addressLine2: e.currentTarget.value })}
          onBlur={(e) =>
            handleTextBlur(
              QUESTION_KEYS.BUSINESS_ADDRESS_LINE_2,
              "Apt., suite, etc",
              e.currentTarget.value,
            )
          }
          size="md"
          classNames={{ input: styles.input }}
        />

        {/* City */}
        <TextInput
          label="City"
          placeholder="City"
          value={data.city}
          onChange={(e) => onChange({ city: e.currentTarget.value })}
          onBlur={(e) =>
            handleTextBlur(
              QUESTION_KEYS.BUSINESS_CITY,
              "City",
              e.currentTarget.value,
            )
          }
          required
          size="md"
          classNames={{ input: styles.input }}
          styles={labelStyles}
        />

        {/* State and Zip Code Row */}
        <Group grow className={styles.rowGroup}>
          <Select
            label="State"
            placeholder="State"
            data={US_STATES}
            value={data.state}
            onChange={(value) => {
              onChange({ state: value || "" });
              if (value) {
                onChange({}, QUESTION_KEYS.BUSINESS_STATE, "State", value);
              }
            }}
            searchable
            required
            size="md"
            classNames={{ input: styles.input }}
            styles={labelStyles}
          />
          <TextInput
            label="Zip Code"
            placeholder="Zip Code"
            value={data.zipCode}
            onChange={(e) => onChange({ zipCode: e.currentTarget.value })}
            onBlur={(e) =>
              handleTextBlur(
                QUESTION_KEYS.BUSINESS_ZIP_CODE,
                "Zip Code",
                e.currentTarget.value,
              )
            }
            required
            size="md"
            classNames={{ input: styles.input }}
            styles={labelStyles}
          />
        </Group>

        {/* Phone number field - uses default "Phone Number" label from StandardizedPhoneInput */}
        {businessPhoneQuestion && (
          <StandardizedPhoneInput
            value={formattedPhoneValue}
            onChange={handlePhoneChange}
            onParsedChange={handlePhoneParsedChange}
            placeholder="(555) 555-5555"
            required
            size="md"
            classNames={{ input: styles.input }}
            styles={labelStyles}
          />
        )}

        {/* What is your timezone? */}
        <Tooltip
          label="We currently only support US-based companies. If you're from another country, please contact us at contact@getpagoda.com"
          position="top"
          multiline
          w={220}
        >
          <Select
            label="What is your Timezone?"
            placeholder="Select timezone"
            data={US_TIMEZONES}
            value={data.timezone}
            onChange={(value) => {
              onChange({ timezone: value || "" });
              if (value) {
                onChange(
                  {},
                  QUESTION_KEYS.WHAT_IS_YOUR_TIMEZONE,
                  "What is your Timezone?",
                  value,
                );
              }
            }}
            searchable
            required
            size="md"
            classNames={{ input: styles.input }}
            styles={labelStyles}
          />
        </Tooltip>

        {/* How many employees do you have? */}
        {employeeCountQuestion && (
          <ChipGroupSingle
            label={employeeCountQuestion.questionText}
            options={
              employeeCountQuestion.options?.map((opt) => ({
                value: opt,
                label: opt,
              })) || []
            }
            value={data.employeeCount}
            onChange={(value) =>
              onChange(
                { employeeCount: value },
                QUESTION_KEYS.HOW_MANY_EMPLOYEES_DO_YOU_HAVE,
                employeeCountQuestion.questionText,
                value || "",
              )
            }
            required
          />
        )}
      </Stack>
    </Stack>
  );
}
